/**
 * @JSON.prune - is a JSON prototype polyfill method to stringify any object without overflow
 * two additional optional parameters :
 *  - the maximal depth (default : 6)
 *  - the maximal length of arrays (default : 50)
 * You can also pass an "options" object.
 *
 * @examples
 * ```ts
 *   var json = JSON.prune(window)
 *   var arr = Array.apply(0,Array(1000));
 *   var json = JSON.prune(arr, 4, 20)
 *   var json = JSON.prune(window.location, {inheritedProperties:true})
 * ...
 * ```
 * @reference - The code is based on Douglas Crockford's code :
 * https://github.com/douglascrockford/JSON-js/blob/master/json2.js
 * No effort was done to support old browsers. JSON.prune will fail on IE8.
 *
 */
(function JsonPrune() {
  'use strict';

  if ((globalThis.JSON as any).prune) return;

  var DEFAULT_MAX_DEPTH = 6;
  var DEFAULT_ARRAY_MAX_LENGTH = 50;
  var DEFAULT_PRUNED_VALUE = '"-pruned-"';
  var seen: any[]; // Same variable used for all stringifications
  var iterator: ((obj: any, callback: any, excluded?: any) => void) | ((arg0: any, arg1: (k: any) => void) => void); // either forEachEnumerableOwnProperty, forEachEnumerableProperty or forEachProperty

  // iterates on enumerable own properties (default behavior)
  var forEachEnumerableOwnProperty = function (obj: any, callback: (arg0: string) => void) {
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) callback(k);
    }
  };
  // iterates on enumerable properties
  var forEachEnumerableProperty = function (obj: any, callback: (arg0: string) => void) {
    for (var k in obj) callback(k);
  };
  // iterates on properties, even non enumerable and inherited ones
  // This is dangerous
  var forEachProperty = function (obj: null, callback: (arg0: string) => void, excluded: { [x: string]: boolean }) {
    if (obj == null) return;
    excluded = excluded || {};
    Object.getOwnPropertyNames(obj).forEach(function (k) {
      if (!excluded[k]) {
        callback(k);
        excluded[k] = true;
      }
    });
    forEachProperty(Object.getPrototypeOf(obj), callback, excluded);
  };

  Object.defineProperty(Date.prototype, 'toPrunedJSON', { value: Date.prototype.toJSON });

  var cx = /[\u0000\u00ad\u0600-\u0604\u070f\u17b4\u17b5\u200c-\u200f\u2028-\u202f\u2060-\u206f\ufeff\ufff0-\uffff]/g,
    escapable =
      /[\\\"\x00-\x1f\x7f-\x9f\u00ad\u0600-\u0604\u070f\u17b4\u17b5\u200c-\u200f\u2028-\u202f\u2060-\u206f\ufeff\ufff0-\uffff]/g,
    meta = {
      // table of character substitutions
      '\b': '\\b',
      '\t': '\\t',
      '\n': '\\n',
      '\f': '\\f',
      '\r': '\\r',
      '"': '\\"',
      '\\': '\\\\',
    } as Record<string, string>;

  function quote(value: string) {
    escapable.lastIndex = 0;
    return escapable.test(value)
      ? '"' +
          value.replace(escapable, function (a: string | number) {
            var c = meta[a];
            return typeof c === 'string' ? c : '\\u' + ('0000' + (a as string).charCodeAt(0).toString(16)).slice(-4);
          }) +
          '"'
      : '"' + value + '"';
  }

  var prune = function (value: any, depthDecr: number, arrayMaxLength: number) {
    var prunedString = DEFAULT_PRUNED_VALUE;
    var replacer: (arg0: any, arg1: string | undefined, arg2: boolean) => any;
    if (typeof depthDecr == 'object') {
      var options: any = depthDecr;
      depthDecr = options.depthDecr;
      arrayMaxLength = options.arrayMaxLength;
      iterator = options.iterator || forEachEnumerableOwnProperty;
      if (options.allProperties) iterator = forEachProperty;
      else if (options.inheritedProperties) iterator = forEachEnumerableProperty;
      if ('prunedString' in options) {
        prunedString = options.prunedString;
      }
      if (options.replacer) {
        replacer = options.replacer;
      }
    } else {
      iterator = forEachEnumerableOwnProperty;
    }
    seen = [];
    depthDecr = depthDecr || DEFAULT_MAX_DEPTH;
    arrayMaxLength = arrayMaxLength || DEFAULT_ARRAY_MAX_LENGTH;
    function str(key: string | number, holder: { [x: string]: any; ''?: any }, depthDecr: number) {
      var i,
        k,
        v,
        length,
        partial: any[],
        value = holder[key];

      if (value && typeof value === 'object' && typeof value.toPrunedJSON === 'function') {
        value = value.toPrunedJSON(key);
      }
      if (value && typeof value.toJSON === 'function') {
        value = value.toJSON();
      }

      switch (typeof value) {
        case 'string':
          return quote(value);
        case 'number':
          return isFinite(value) ? String(value) : 'null';
        case 'boolean':
        case 'null' as any:
          return String(value);
        case 'object':
          if (!value) {
            return 'null';
          }
          if (depthDecr <= 0 || seen.indexOf(value) !== -1) {
            if (replacer) {
              var replacement = replacer(value, prunedString, true);
              return replacement === undefined ? undefined : '' + replacement;
            }
            return prunedString;
          }
          seen.push(value);
          partial = [];
          if (Object.prototype.toString.apply(value) === '[object Array]') {
            length = Math.min(value.length, arrayMaxLength);
            for (i = 0; i < length; i += 1) {
              partial[i] = str(i, value, depthDecr - 1) || 'null';
            }
            v = '[' + partial.join(',') + ']';
            if (replacer && value.length > arrayMaxLength) return replacer(value, v, false);
            return v;
          }
          if (value instanceof RegExp) {
            return quote(value.toString());
          }
          iterator(value, function (k: any) {
            try {
              v = str(k, value, depthDecr - 1);
              if (v) partial.push(quote(k) + ':' + v);
            } catch (e) {
              // this try/catch due to forbidden accessors on some objects
            }
          });
          return '{' + partial.join(',') + '}';
        case 'function':
        case 'undefined':
          return replacer ? replacer(value, undefined, false) : undefined;
      }
    }
    return str('', { '': value }, depthDecr);
  };

  (prune as any).log = function () {
    console.log.apply(
      console,
      Array.prototype.map.call(arguments, function (v) {
        return JSON.parse((JSON as any).prune(v));
      })
    );
  };
  (prune as any).forEachProperty = forEachProperty; // you might want to also assign it to Object.forEachProperty

  if (typeof module !== 'undefined') module.exports = prune;
  else (JSON as any).prune = prune;
})();
